import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Checkbox,
  FormControlLabel,
  Tabs,
  Tab,
  Tooltip,
  Alert,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Add as AddIcon, Remove as RemoveIcon, Search as SearchIcon, Close as CloseIcon, LocationOn as LocationIcon, DeleteOutline as DeleteIcon, SwapHoriz as SwapIcon, ContentCopy as CopyIcon } from '@mui/icons-material';
import Autocomplete from '@mui/material/Autocomplete';
import InputAdornment from '@mui/material/InputAdornment';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import { fetchSurveyGps, createSurveyGps, updateSurveyGps, deleteSurveyGps, fetchRegions, fetchBusinessUnits, fetchTrackerLocation, fetchRoverNtrip, switchRoverProvider } from '../services/api';
import { useAuth, canEditEquipment } from '../context/AuthContext';
import TrackerSerialField from '../components/TrackerSerialField';
import SwitchProviderDialog, { PROVIDER_LABEL, otherProvider } from '../components/SwitchProviderDialog';

L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const ROVER_MODELS = ['Emlid RS3', 'Emlid RX2', 'Emlid RS4', 'Emlid RS4 Pro'];
// Model dropdown choice that opens a free-text model, for rovers that aren't Emlids.
const OTHER_MODEL = '__other__';

const MODEL_IMAGE = {
  'Emlid RS3': '/data/emlid_units/emlid_rs3.png',
  'Emlid RX2': '/data/emlid_units/emlid_RX2.png',
  'Emlid RS4': '/data/emlid_units/emlid_rs4.png',
  'Emlid RS4 Pro': '/data/emlid_units/emlid_rs4_pro.png',
};

const ROVER_STATUSES = ['Active', 'Damaged', 'Decommissioned'];

const EMPTY_FORM = {
  menard_internal_number: '',
  status: 'Active',
  model: '',
  custom_model: '',
  purchase_date: '',
  serial_number: '',
  region: '',
  biz_unit: '',
  contact: '',
  ntrip_server: '',
  ntrip_username: '',
  ntrip_password: '',
  ntrip_mountpoint: '',
  latitude: '',
  longitude: '',
  has_phone: false,
  phone_make: '',
  phone_model: '',
  phone_imei: '',
  phone_sim: '',
  phone_email: '',
  has_tracker: false,
  tracker_serial: '',
  notes: '',
};

// Recenter map when position changes
function MapUpdater({ center }) {
  const map = useMap();
  useEffect(() => { map.setView(center, map.getZoom()); }, [center, map]);
  return null;
}

// Full interactive map for dialog
function DetailMap({ lat, lng, darkMode }) {
  return (
    <Box sx={{ height: 200, width: '100%', maxWidth: 260, borderRadius: 1, overflow: 'hidden', border: 1, borderColor: 'divider' }}>
      <MapContainer
        center={[lat, lng]}
        zoom={12}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom
        zoomControl={false}
        attributionControl={false}
      >
        <TileLayer
          url={darkMode
            ? 'https://tile.jawg.io/jawg-dark/{z}/{x}/{y}{r}.png?access-token=6FXDBzhanSef1HN4s1LZ3mPreirTHg2H5ErbrBakZuSCBpEww5wULxbgjz9EzrE6'
            : 'https://tile.jawg.io/jawg-streets/{z}/{x}/{y}{r}.png?access-token=6FXDBzhanSef1HN4s1LZ3mPreirTHg2H5ErbrBakZuSCBpEww5wULxbgjz9EzrE6'
          }
        />
        <Marker position={[lat, lng]} />
        <MapUpdater center={[lat, lng]} />
      </MapContainer>
    </Box>
  );
}

function GpsRovers() {
  const { user } = useAuth();
  const canEdit = canEditEquipment(user?.role);
  const darkMode = useTheme().palette.mode === 'dark';

  // Relative "x ago" for the Samsara tracker's last GPS fix (last_update_iso is UTC).
  const timeAgo = (iso) => {
    if (!iso) return 'Never';
    const then = new Date(iso).getTime();
    if (isNaN(then)) return '—';
    const s = Math.max(0, Math.floor((Date.now() - then) / 1000));
    if (s < 60) return 'just now';
    const m = Math.floor(s / 60);
    if (m < 60) return `${m} minute${m !== 1 ? 's' : ''} ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h} hour${h !== 1 ? 's' : ''} ago`;
    const d = Math.floor(h / 24);
    return `${d} day${d !== 1 ? 's' : ''} ago`;
  };

  // Returns true only if user can edit this specific rover (same business unit)
  const canEditRover = (rover) => canEdit && (!rover?.biz_unit || !user?.businessUnit || rover.biz_unit === user.businessUnit);

  const [rovers, setRovers] = useState([]);
  const [regions, setRegions] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState(0);

  // Add / Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editRover, setEditRover] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  // NTRIP license signed out to the open rover (null = none), and the provider switch
  const [roverNtrip, setRoverNtrip] = useState(null);
  const [ntripLoading, setNtripLoading] = useState(false);
  const [switchOpen, setSwitchOpen] = useState(false);
  const [switchNotice, setSwitchNotice] = useState('');

  // Setup instructions dialog (shown after adding a new rover)
  const [setupOpen, setSetupOpen] = useState(false);
  const [assignedNumber, setAssignedNumber] = useState('');

  // Delete dialog (global "Remove" picker)
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Per-card delete confirmation (BU admin only) — holds the rover being deleted
  const [confirmDeleteRover, setConfirmDeleteRover] = useState(null);

  // Admins can delete any rover; BU admins only within their own business unit.
  const canDeleteRover = (rover) =>
    user?.role === 'admin' ||
    (user?.role === 'bu_manager' && !!rover?.biz_unit && rover.biz_unit === user?.businessUnit);

  const loadRovers = async () => {
    try {
      const roverData = await fetchSurveyGps();
      setRovers(roverData.rovers || []);
    } catch (err) {
      console.error('Error loading rovers:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRovers();
    fetchRegions(undefined, user?.country).then(d => setRegions(d.regions || [])).catch(() => {});
    fetchBusinessUnits(user?.country).then(d => setBusinessUnits(d.businessUnits || [])).catch(() => {});
  }, []);

  const activeRovers = useMemo(() => rovers.filter(r => r.status !== 'Decommissioned'), [rovers]);
  const decommissionedRovers = useMemo(() => rovers.filter(r => r.status === 'Decommissioned'), [rovers]);

  const filteredRovers = useMemo(() => {
    const source = tab === 0 ? activeRovers : decommissionedRovers;
    if (!search.trim()) return source;
    const term = search.toLowerCase();
    return source.filter((r) =>
      (r.menard_internal_number || '').toLowerCase().includes(term) ||
      (r.model || '').toLowerCase().includes(term) ||
      (r.serial_number || '').toLowerCase().includes(term) ||
      (r.contact || '').toLowerCase().includes(term)
    );
  }, [rovers, search, tab, activeRovers, decommissionedRovers]);

  // Grouping is country-aware and strictly scoped to the user's own Business Unit.
  // Everyone groups by Region. For US users this is the per-region breakdown
  // inside their own BU (previously they grouped by BU, which collapsed to a
  // single heading); other countries already grouped by Region — unchanged.
  // Empty groups never appear because groups are built from the actual rovers.
  // Order: General first, then alphabetical, Unassigned last.
  const groupedRovers = useMemo(() => {
    const userBu = user?.businessUnit || '';
    // Strict scoping: a user only ever sees rovers in their own BU (admins included).
    // Fall back to all rovers only when the account has no BU set, to avoid an empty page.
    const scoped = userBu ? filteredRovers.filter(r => r.biz_unit === userBu) : filteredRovers;

    const groups = {};
    for (const r of scoped) {
      const key = r.region || '__unassigned__';
      if (!groups[key]) groups[key] = [];
      groups[key].push(r);
    }
    return Object.entries(groups).sort(([a], [b]) => {
      if (a === '__unassigned__') return 1;
      if (b === '__unassigned__') return -1;
      if (a === 'General') return -1;
      if (b === 'General') return 1;
      return a.localeCompare(b);
    });
  }, [filteredRovers, user]);

  const openAdd = () => {
    setEditRover(null);
    setEditForm(EMPTY_FORM);
    setSaveError('');
    setEditOpen(true);
  };

  const openEdit = (rover) => {
    setEditRover({ ...rover, _canEdit: canEditRover(rover) });
    setEditForm({
      menard_internal_number: rover.menard_internal_number || '',
      status: rover.status || 'Active',
      purchase_date: rover.purchase_date ? rover.purchase_date.split('T')[0] : '',
      has_phone: !!rover.phone_make,
      phone_make: rover.phone_make || '',
      phone_model: rover.phone_model || '',
      phone_imei: rover.phone_imei || '',
      phone_sim: rover.phone_sim || '',
      phone_email: rover.phone_email || '',
      has_tracker: !!rover.tracker_serial,
      tracker_serial: rover.tracker_serial || '',
      notes: rover.notes || '',
      model: rover.model || '',
      serial_number: rover.serial_number || '',
      region: rover.region || '',
      biz_unit: rover.biz_unit || '',
      contact: rover.contact || '',
      ntrip_server: rover.ntrip_server || '',
      ntrip_username: rover.ntrip_username || '',
      ntrip_password: rover.ntrip_password || '',
      ntrip_mountpoint: rover.ntrip_mountpoint || '',
      latitude: rover.latitude != null ? String(rover.latitude) : '',
      longitude: rover.longitude != null ? String(rover.longitude) : '',
    });
    setSaveError('');
    setSwitchNotice('');
    setEditOpen(true);
    loadRoverNtrip(rover.id);

    // Region choices are BU-scoped — load the list for this rover's BU
    if (rover.biz_unit) {
      fetchRegions(rover.biz_unit, user?.country).then(d => setRegions(d.regions || [])).catch(() => {});
    }

    // On card open, pull a fresh Samsara fix for this rover's tracker so the map
    // and last-update reflect the latest position rather than the cached list
    // value. Guarded by id so a quick re-open of another card doesn't clobber it.
    if (rover.tracker_serial) {
      fetchTrackerLocation(rover.tracker_serial)
        .then((res) => {
          if (res?.tracker_location) {
            setEditRover((prev) => (prev && prev.id === rover.id
              ? { ...prev, tracker_location: res.tracker_location }
              : prev));
          }
        })
        .catch(() => { /* keep the cached list value on failure */ });
    }
  };

  const loadRoverNtrip = (roverId) => {
    setRoverNtrip(null);
    setNtripLoading(true);
    fetchRoverNtrip(roverId)
      .then(res => setRoverNtrip(res.ntrip))
      .catch(() => setRoverNtrip(null))
      .finally(() => setNtripLoading(false));
  };

  const handleSwitchProvider = async () => {
    const res = await switchRoverProvider(editRover.id);
    setSwitchNotice(`Switched to ${PROVIDER_LABEL[res.provider]}. Enter the new credentials on the receiver.`);
    loadRoverNtrip(editRover.id);
  };

  const handleEditClose = () => {
    setEditOpen(false);
    setEditRover(null);
    setSaveError('');
  };

  const handleSave = async () => {
    const missing = [...(!editForm.biz_unit ? ['Business Unit'] : []), ...(!editForm.region ? ['Region'] : [])];
    if (missing.length) {
      setSaveError(`${missing.join(' and ')} ${missing.length > 1 ? 'are' : 'is'} required`);
      return;
    }
    const model = editForm.model === OTHER_MODEL ? editForm.custom_model.trim() : editForm.model;
    if (editForm.model === OTHER_MODEL && !model) {
      setSaveError('Enter the model');
      return;
    }
    setSaving(true);
    setSaveError('');
    try {
      const payload = {
        ...editForm,
        model,
        menard_internal_number: editForm.menard_internal_number.trim(),
        latitude: editForm.latitude !== '' ? parseFloat(editForm.latitude) : null,
        longitude: editForm.longitude !== '' ? parseFloat(editForm.longitude) : null,
      };
      delete payload.custom_model;
      if (editRover) {
        await updateSurveyGps(editRover.id, payload);
        await loadRovers();
        handleEditClose();
      } else {
        const result = await createSurveyGps(payload);
        await loadRovers();
        handleEditClose();
        setAssignedNumber(result.internal_number || '');
        setSetupOpen(true);
      }
    } catch (err) {
      setSaveError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await deleteSurveyGps(deleteId);
      await loadRovers();
      setDeleteOpen(false);
      setDeleteId('');
    } catch (err) {
      console.error('Error deleting rover:', err);
    } finally {
      setDeleting(false);
    }
  };

  // Per-card delete (BU admin). Soft-deletes via IsActive=0 on the backend.
  const handleConfirmDelete = async () => {
    if (!confirmDeleteRover) return;
    setDeleting(true);
    try {
      await deleteSurveyGps(confirmDeleteRover.id);
      await loadRovers();
      setConfirmDeleteRover(null);
      handleEditClose();
    } catch (err) {
      console.error('Error deleting rover:', err);
    } finally {
      setDeleting(false);
    }
  };

  const handleChange = (field) => (e) =>
    setEditForm(prev => ({ ...prev, [field]: e.target.value }));

  const dialogLat = parseFloat(editForm.latitude);
  const dialogLng = parseFloat(editForm.longitude);
  const dialogHasPos = !isNaN(dialogLat) && !isNaN(dialogLng);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Tabs */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 0.5, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Active" />
        <Tab label="Decommissioned" />
      </Tabs>

      {/* Controls Row */}
      <Grid container spacing={2} sx={{ mb: 0.5 }}>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: { xs: 'flex-start', sm: 'flex-end' }, height: '100%' }}>
            <TextField
              size="small"
              variant="outlined"
              placeholder="Search rovers..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{
                width: 260,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.paper',
                  '& fieldset': { borderWidth: 2, borderColor: 'primary.main' },
                  '&:hover fieldset': { borderColor: 'primary.dark' },
                  '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: 'primary.main' }} />
                  </InputAdornment>
                ),
              }}
            />
            {canEdit && (
              <>
                <Tooltip title="Add Rover" arrow>
                  <IconButton
                    onClick={openAdd}
                    sx={{ backgroundColor: 'secondary.main', color: 'white', '&:hover': { backgroundColor: 'secondary.dark' }, width: 36, height: 36 }}
                  >
                    <AddIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Remove Rover" arrow>
                  <IconButton
                    onClick={() => { setDeleteId(''); setDeleteOpen(true); }}
                    sx={{ backgroundColor: 'error.main', color: 'white', '&:hover': { backgroundColor: 'error.dark' }, width: 36, height: 36 }}
                  >
                    <RemoveIcon />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Grouped by Region */}
      {groupedRovers.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No rovers found.</Typography>
        </Paper>
      )}

      {groupedRovers.map(([buKey, buRovers]) => (
        <Box key={buKey} sx={{ mb: 4 }}>
          {/* Group heading — Region (scoped to the user's own BU) */}
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="h6" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
              {buKey === '__unassigned__' ? 'Unassigned' : buKey}
            </Typography>
          </Box>
          <Box sx={{ mb: 2, height: '2px', backgroundColor: darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.5)' }} />

          <Grid container spacing={1.5}>
            {buRovers.map((rover) => {
              const lat = parseFloat(rover.latitude);
              const lng = parseFloat(rover.longitude);
              const hasPos = !isNaN(lat) && !isNaN(lng);
              return (
                <Grid item xs={6} sm={4} md={3} lg={2} key={rover.id}>
                  <Card
                    onClick={() => openEdit(rover)}
                    sx={{
                      border: 1,
                      borderColor: rover.status === 'Damaged' ? '#ff8c00' : rover.linked_license ? 'primary.main' : 'divider',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
                    }}
                  >
                    <CardContent sx={{ p: '10px !important' }}>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                        {/* Info */}
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="body2" fontWeight={700} noWrap sx={{ mb: 0.25 }}>
                            {rover.menard_internal_number || '—'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', mb: 0.25 }}>
                            {rover.model || '—'}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ display: 'block', color: 'text.secondary', whiteSpace: { xs: 'normal', sm: 'nowrap' }, overflowWrap: 'anywhere' }}
                          >
                            {rover.contact || '—'}
                          </Typography>
                          {/* Live city/state from the Samsara tracker, styled like Assigned To */}
                          {rover.tracker_location?.city_state && (
                            <Typography
                              variant="caption"
                              sx={{ display: 'block', color: 'text.secondary', whiteSpace: { xs: 'normal', sm: 'nowrap' }, overflowWrap: 'anywhere' }}
                            >
                              {rover.tracker_location.city_state}
                            </Typography>
                          )}
                          {hasPos && (
                            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mt: 0.5 }}>
                              <LocationIcon sx={{ fontSize: 12, color: 'primary.main' }} />
                              <Typography variant="caption" color="primary.main" noWrap>
                                {lat.toFixed(3)}, {lng.toFixed(3)}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                        {/* Model image */}
                        {MODEL_IMAGE[rover.model] && (
                          <Box component="img" src={MODEL_IMAGE[rover.model]} alt={rover.model} sx={{ height: { xs: 64, sm: 90 }, width: { xs: 42, sm: 60 }, objectFit: 'contain', flexShrink: 0 }} />
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      ))}

      {/* Add / Edit Dialog */}
      {/* ══ ADD ROVER DIALOG (form layout) ══════════════════════════════ */}
      <Dialog open={editOpen && !editRover} onClose={handleEditClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" fontWeight={700}>Add Rover</Typography>
          <IconButton onClick={handleEditClose} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0, mt: 1 }}>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Menard Number"
                  size="small"
                  fullWidth
                  value={editForm.menard_internal_number}
                  onChange={handleChange('menard_internal_number')}
                  placeholder="Next SV number"
                  InputLabelProps={{ shrink: true }}
                  helperText="Leave blank to assign the next SV number"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select value={editForm.status} label="Status" onChange={handleChange('status')}>
                    {ROVER_STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Purchase Date" type="date" size="small" fullWidth value={editForm.purchase_date} onChange={handleChange('purchase_date')} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Model</InputLabel>
                  <Select value={editForm.model} label="Model" onChange={handleChange('model')}>
                    <MenuItem value=""><em>None</em></MenuItem>
                    {ROVER_MODELS.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                    <MenuItem value={OTHER_MODEL}>Other…</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {editForm.model === OTHER_MODEL && (
                <Grid item xs={12} sm={6}>
                  <TextField label="Custom Model" size="small" fullWidth autoFocus value={editForm.custom_model} onChange={handleChange('custom_model')} placeholder="e.g. Trimble R12i" />
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <TextField label="Serial Number" size="small" fullWidth value={editForm.serial_number} onChange={handleChange('serial_number')} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl size="small" fullWidth>
                  <InputLabel shrink>Business Unit</InputLabel>
                  <Select
                    value={editForm.biz_unit}
                    label="Business Unit"
                    displayEmpty
                    renderValue={(v) => v || <em>Select business unit</em>}
                    onChange={(e) => {
                      const bu = e.target.value;
                      // Clear the region — the current value may not belong to the new BU.
                      setEditForm(prev => ({ ...prev, biz_unit: bu, region: '' }));
                      fetchRegions(bu, user?.country).then(d => setRegions(d.regions || [])).catch(() => {});
                    }}
                  >
                    {businessUnits.map((b) => <MenuItem key={b} value={b}>{b}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl size="small" fullWidth>
                  <InputLabel shrink>Region</InputLabel>
                  <Select
                    value={editForm.region}
                    label="Region"
                    displayEmpty
                    disabled={!editForm.biz_unit}
                    renderValue={(v) => v || <em>{editForm.biz_unit ? 'Select region' : 'Select business unit first'}</em>}
                    onChange={handleChange('region')}
                  >
                    {[...regions, ...(!regions.includes('General') ? ['General'] : [])].map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Assigned To" size="small" fullWidth value={editForm.contact} onChange={handleChange('contact')} />
              </Grid>
            </Grid>

            <Divider sx={{ mb: 1 }} />
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editForm.has_phone}
                      onChange={(e) => setEditForm(prev => ({ ...prev, has_phone: e.target.checked }))}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">Include Phone</Typography>}
                  sx={{ mb: editForm.has_phone ? 1 : 0 }}
                />
                {editForm.has_phone && (
                  <Grid container spacing={2}>
                    {[
                      { label: 'Phone Make', field: 'phone_make' },
                      { label: 'Phone Model', field: 'phone_model' },
                      { label: 'IMEI', field: 'phone_imei' },
                      { label: 'SIM Number', field: 'phone_sim' },
                      { label: 'Email Account', field: 'phone_email' },
                    ].map(({ label, field }) => (
                      <Grid item xs={12} key={field}>
                        <TextField label={label} size="small" fullWidth value={editForm[field]} onChange={handleChange(field)} />
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
              <Box sx={{ flex: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editForm.has_tracker}
                      onChange={(e) => setEditForm(prev => ({ ...prev, has_tracker: e.target.checked }))}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">Include Tracker</Typography>}
                  sx={{ mb: editForm.has_tracker ? 1 : 0 }}
                />
                {editForm.has_tracker && (
                  <TrackerSerialField label="Tracker Serial Number" size="small" fullWidth value={editForm.tracker_serial} onChange={handleChange('tracker_serial')} kind="rover" />
                )}
              </Box>
            </Box>

            {editForm.status === 'Decommissioned' && (
              <>
                <Typography variant="overline" color="text.secondary" fontWeight={700} sx={{ letterSpacing: 1, mt: 3 }}>Notes</Typography>
                <Divider sx={{ mb: 2 }} />
                <TextField size="small" fullWidth multiline minRows={2} maxRows={6} value={editForm.notes} onChange={handleChange('notes')} />
              </>
            )}

            {saveError && <Typography color="error" variant="body2" sx={{ mt: 1 }}>{saveError}</Typography>}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleEditClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Add Rover'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══ EDIT ROVER DIALOG ════════════════════════════════════════════ */}
      {(() => { const dialogCanEdit = editRover ? !!editRover._canEdit : canEdit; return (
      <Dialog open={editOpen && !!editRover} onClose={handleEditClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h5" fontWeight={700}>{editRover?.menard_internal_number || 'Rover'}</Typography>
          <IconButton onClick={handleEditClose} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          {/* Details beside the photo/map on desktop; stacked on a phone, where
              the fixed 260px column would otherwise overflow the dialog. */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3, mt: 1 }}>
            {/* Left column: Details */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Section: Details */}
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: -1 }}>Details</Typography>
              {dialogCanEdit ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Status</InputLabel>
                      <Select value={editForm.status} label="Status" onChange={handleChange('status')}>
                        {ROVER_STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField label="Purchase Date" type="date" size="small" fullWidth value={editForm.purchase_date} onChange={handleChange('purchase_date')} InputLabelProps={{ shrink: true }} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField label="Model" size="small" fullWidth value={editForm.model} InputProps={{ readOnly: true }} sx={{ pointerEvents: 'none', '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: 'rgba(255,255,255,0.23)' } } }} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField label="Serial Number" size="small" fullWidth value={editForm.serial_number} InputProps={{ readOnly: true, disableUnderline: false }} sx={{ pointerEvents: 'none', '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: 'rgba(255,255,255,0.23)' } } }} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl size="small" fullWidth>
                      <InputLabel shrink>Business Unit</InputLabel>
                      <Select
                        value={editForm.biz_unit}
                        label="Business Unit"
                        displayEmpty
                        renderValue={(v) => v || <em>Select business unit</em>}
                        onChange={(e) => {
                          const bu = e.target.value;
                          // Clear the region — the current value may not belong to the new BU.
                          setEditForm(prev => ({ ...prev, biz_unit: bu, region: '' }));
                          fetchRegions(bu, user?.country).then(d => setRegions(d.regions || [])).catch(() => {});
                        }}
                      >
                        {businessUnits.map((b) => <MenuItem key={b} value={b}>{b}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl size="small" fullWidth>
                      <InputLabel shrink>Region</InputLabel>
                      <Select
                        value={editForm.region}
                        label="Region"
                        displayEmpty
                        disabled={!editForm.biz_unit}
                        renderValue={(v) => v || <em>{editForm.biz_unit ? 'Select region' : 'Select business unit first'}</em>}
                        onChange={handleChange('region')}
                      >
                        {[...regions, ...(!regions.includes('General') ? ['General'] : [])].map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField label="Assigned To" size="small" fullWidth value={editForm.contact} onChange={handleChange('contact')} />
                  </Grid>
                  {editRover?.linked_license && (
                    <Grid item xs={12}>
                      <TextField label="License" size="small" fullWidth value={editRover.linked_license} InputProps={{ readOnly: true }} helperText="Assigned from GPS Licenses page" />
                    </Grid>
                  )}
                </Grid>
              ) : (
                <Grid container spacing={2}>
                  {[
                    { label: 'Status', field: 'status' },
                    { label: 'Purchase Date', field: 'purchase_date' },
                    { label: 'Model', field: 'model' },
                    { label: 'Serial Number', field: 'serial_number' },
                    { label: 'Business Unit', field: 'biz_unit' },
                    { label: 'Region', field: 'region' },
                    { label: 'Assigned To', field: 'contact' },
                  ].map(({ label, field }) => (
                    <Grid item xs={6} key={field}>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</Typography>
                      <Typography variant="body1" fontWeight={500}>{editForm[field] || '—'}</Typography>
                    </Grid>
                  ))}
                  {editRover?.linked_license && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>License</Typography>
                      <Typography variant="body1" fontWeight={500} color="primary.main">{editRover.linked_license}</Typography>
                    </Grid>
                  )}
                </Grid>
              )}

              {/* Phone + Tracker sections (side-by-side) */}
              {dialogCanEdit ? (
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 3 }, alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={editForm.has_phone}
                          onChange={(e) => setEditForm(prev => ({ ...prev, has_phone: e.target.checked }))}
                          size="small"
                        />
                      }
                      label={<Typography variant="body2" fontWeight={600}>Include Phone</Typography>}
                    />
                    {editForm.has_phone && (
                      <Grid container spacing={2}>
                        {[
                          { label: 'Phone Make', field: 'phone_make' },
                          { label: 'Phone Model', field: 'phone_model' },
                          { label: 'IMEI', field: 'phone_imei' },
                          { label: 'SIM Number', field: 'phone_sim' },
                          { label: 'Email Account', field: 'phone_email' },
                        ].map(({ label, field }) => (
                          <Grid item xs={12} key={field}>
                            <TextField label={label} size="small" fullWidth value={editForm[field]} onChange={handleChange(field)} />
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={editForm.has_tracker}
                          onChange={(e) => setEditForm(prev => ({ ...prev, has_tracker: e.target.checked }))}
                          size="small"
                        />
                      }
                      label={<Typography variant="body2" fontWeight={600}>Include Tracker</Typography>}
                    />
                    {editForm.has_tracker && (
                      <TrackerSerialField
                        label="Tracker Serial Number"
                        size="small"
                        fullWidth
                        value={editForm.tracker_serial}
                        onChange={handleChange('tracker_serial')}
                        kind="rover"
                        unitId={editRover?.id}
                        originalValue={editRover?.tracker_serial}
                      />
                    )}
                  </Box>
                </Box>
              ) : (
                (editForm.has_phone || editForm.has_tracker) && (
                  <>
                    <Divider />
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 3 }, alignItems: 'flex-start' }}>
                      {editForm.has_phone && (
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Phone</Typography>
                          <Grid container spacing={2}>
                            {[
                              { label: 'Phone Make', field: 'phone_make' },
                              { label: 'Phone Model', field: 'phone_model' },
                              { label: 'IMEI', field: 'phone_imei' },
                              { label: 'SIM Number', field: 'phone_sim' },
                              { label: 'Email Account', field: 'phone_email' },
                            ].map(({ label, field }) => (
                              <Grid item xs={12} key={field}>
                                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</Typography>
                                <Typography variant="body1" fontWeight={500}>{editForm[field] || '—'}</Typography>
                              </Grid>
                            ))}
                          </Grid>
                        </Box>
                      )}
                      {editForm.has_tracker && (
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Tracker</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Tracker Serial Number</Typography>
                          <Typography variant="body1" fontWeight={500}>{editForm.tracker_serial || '—'}</Typography>
                        </Box>
                      )}
                    </Box>
                  </>
                )
              )}

              {/* NTRIP — the license signed out to this rover from the GPS Licenses page */}
              <Divider />
              <Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>
                    NTRIP{roverNtrip ? ` · ${PROVIDER_LABEL[roverNtrip.provider]}` : ''}
                  </Typography>
                  {roverNtrip?.can_switch && (
                    <Button size="small" variant="outlined" startIcon={<SwapIcon />} onClick={() => setSwitchOpen(true)} sx={{ whiteSpace: 'nowrap' }}>
                      Switch to {PROVIDER_LABEL[otherProvider(roverNtrip.provider)]}
                    </Button>
                  )}
                </Box>
                {switchNotice && (
                  <Alert severity="success" onClose={() => setSwitchNotice('')} sx={{ mb: 1.5 }}>{switchNotice}</Alert>
                )}
                {ntripLoading ? (
                  <CircularProgress size={20} />
                ) : !roverNtrip ? (
                  <Typography variant="body2" color="text.secondary">No license is signed out to this rover.</Typography>
                ) : (
                  <>
                    <Grid container spacing={1.5}>
                      {[
                        ['Server', roverNtrip.credentials?.server],
                        ['Port', roverNtrip.credentials?.port],
                        ['Username', roverNtrip.credentials?.username],
                        ['Password', roverNtrip.credentials?.password],
                        ['Mountpoint', roverNtrip.credentials?.mountpoint],
                      ].map(([label, value]) => (
                        <Grid item xs={12} sm={6} key={label}>
                          <TextField
                            label={label}
                            size="small"
                            fullWidth
                            value={value || ''}
                            InputProps={{
                              readOnly: true,
                              endAdornment: value ? (
                                <InputAdornment position="end">
                                  <IconButton size="small" onClick={() => navigator.clipboard.writeText(value)} sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}>
                                    <CopyIcon fontSize="small" />
                                  </IconButton>
                                </InputAdornment>
                              ) : null,
                            }}
                          />
                        </Grid>
                      ))}
                    </Grid>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Signed out by {roverNtrip.holder || '—'}
                      {roverNtrip.expires_at ? ` until ${new Date(roverNtrip.expires_at).toLocaleDateString()}` : ''}
                    </Typography>
                  </>
                )}
              </Box>

              {editForm.status === 'Decommissioned' && (
                dialogCanEdit ? (
                  <TextField label="Notes" size="small" fullWidth multiline minRows={2} maxRows={10} value={editForm.notes} onChange={handleChange('notes')} />
                ) : (
                  editForm.notes && <>
                    <Typography variant="subtitle2" fontWeight={700}>Notes</Typography>
                    <Typography variant="body2" color="text.secondary">{editForm.notes}</Typography>
                  </>
                )
              )}

              {saveError && <Typography color="error" variant="body2">{saveError}</Typography>}
            </Box>

            {/* Right column: Image + Map */}
            <Box sx={{ width: { xs: '100%', sm: 260 }, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center', justifyContent: 'flex-start' }}>
              {/* The picture is hidden on a phone, where it pushed the details off screen */}
              {MODEL_IMAGE[editForm.model] ? (
                <Box sx={{ display: { xs: 'none', sm: 'flex' }, justifyContent: 'center' }}>
                  <Box component="img" src={MODEL_IMAGE[editForm.model]} alt={editForm.model} sx={{ height: 260, objectFit: 'contain' }} />
                </Box>
              ) : (
                <Box sx={{ height: 260, display: { xs: 'none', sm: 'flex' }, alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                  <Typography variant="body2" color="text.disabled" fontStyle="italic">No image</Typography>
                </Box>
              )}
              {editForm.has_tracker && (() => {
                const tl = editRover?.tracker_location;
                const tLat = tl ? parseFloat(tl.lat) : NaN;
                const tLng = tl ? parseFloat(tl.lon) : NaN;
                const hasTrackerPos = !isNaN(tLat) && !isNaN(tLng);
                return (
                  <>
                    {hasTrackerPos ? (
                      <>
                        <DetailMap lat={tLat} lng={tLng} darkMode={darkMode} />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ alignSelf: 'flex-start' }}
                        >
                          Updated {timeAgo(tl.last_update_iso)}
                        </Typography>
                      </>
                    ) : (
                      <Box sx={{
                        height: 200, width: '100%', maxWidth: 260, borderRadius: 1, border: 1, borderColor: 'divider',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        backgroundColor: 'action.hover', gap: 1,
                      }}>
                        <Typography variant="body2" color="text.disabled" fontStyle="italic">Awaiting first fix</Typography>
                      </Box>
                    )}
                  </>
                );
              })()}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {editRover && canDeleteRover(editRover) && (
            <Button color="error" startIcon={<DeleteIcon />} onClick={() => setConfirmDeleteRover(editRover)} sx={{ mr: 'auto' }}>
              Delete
            </Button>
          )}
          <Button onClick={handleEditClose}>{dialogCanEdit ? 'Cancel' : 'Close'}</Button>
          {dialogCanEdit && (
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      )})()}

      <SwitchProviderDialog
        open={switchOpen}
        unitName={editRover?.menard_internal_number}
        provider={roverNtrip?.provider}
        onClose={() => setSwitchOpen(false)}
        onConfirm={handleSwitchProvider}
      />

      {/* ══ SETUP INSTRUCTIONS DIALOG ════════════════════════════════════ */}
      <Dialog open={setupOpen} onClose={() => setSetupOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" fontWeight={700}>Unit Setup Required</Typography>
          <IconButton onClick={() => setSetupOpen(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {/* Assigned number highlight */}
            <Paper sx={{ p: 1, textAlign: 'center', backgroundColor: 'action.hover', border: 1, borderColor: 'primary.main', borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>Menard Internal Number</Typography>
              <Typography variant="h3" fontWeight={700} color="primary.main" sx={{ mt: 0.25 }}>{assignedNumber}</Typography>
            </Paper>

            <Typography variant="body2" color="text.secondary">
              Before moving on, please complete the steps below to ensure the unit is properly identified in the system.
            </Typography>

            {/* Step 1 */}
            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>1. Rename the receiver in the software</Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography component="li" variant="body2">Connect to the receiver</Typography>
                <Typography component="li" variant="body2">Navigate to <Box component="span" sx={{ fontWeight: 600 }}>Settings → Receiver Info</Box></Typography>
                <Typography component="li" variant="body2">Update the receiver name to your Menard internal number</Typography>
              </Box>
            </Box>

            <Divider />

            {/* Step 2 */}
            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>2. Label the physical unit</Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography component="li" variant="body2">Apply a sticker with the same equipment number on the outside of the receiver</Typography>
                <Typography component="li" variant="body2">Place it in a visible and durable location, as shown in the example</Typography>
              </Box>
              <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'center' }}>
                <Box
                  component="img"
                  src={darkMode ? '/darkmode_label.png' : '/lightmode_label.jpg'}
                  alt="Label placement example"
                  sx={{ maxWidth: '100%', maxHeight: 260, borderRadius: 1, border: 1, borderColor: 'divider', objectFit: 'contain' }}
                />
              </Box>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              This ensures the receiver can be easily identified in both the system and in the field.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={() => setSetupOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Remove Rover</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>Select the rover to remove</Typography>
          <FormControl fullWidth size="small">
            <InputLabel>Rover</InputLabel>
            <Select value={deleteId} label="Rover" onChange={(e) => setDeleteId(e.target.value)}>
              {rovers.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.menard_internal_number || r.serial_number || `ID ${r.id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting || !deleteId}>
            {deleting ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══ PER-CARD DELETE CONFIRMATION (BU admin) ══════════════════════ */}
      <Dialog open={!!confirmDeleteRover} onClose={() => !deleting && setConfirmDeleteRover(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Rover?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Are you sure you want to delete this rover? This action removes it from the list.
          </Typography>
          <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: 'action.hover' }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block' }}>
              Internal Number
            </Typography>
            <Typography variant="body1" fontWeight={700}>{confirmDeleteRover?.menard_internal_number || '—'}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mt: 1 }}>
              Serial Number
            </Typography>
            <Typography variant="body1" fontWeight={500}>{confirmDeleteRover?.serial_number || '—'}</Typography>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmDeleteRover(null)} disabled={deleting}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleConfirmDelete} disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default GpsRovers;
